import { useCallback } from 'react';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { InternalErrorCode } from 'fym-common/src/errors/internalErrorCodes';
import { usePublishSmartLinkMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';

interface UsePublishPageProps {
  accountId: number;
  smartLinkId: number;
}

/**
 * Publishing a draft from the manage screen, so the checklist can fix what it
 * found instead of only naming it.
 *
 * One case cannot be fixed here: another page already holds the slug. That
 * needs a new slug, which only the Creator can set, so the artist is taken
 * there rather than shown an error they cannot act on.
 */
export function usePublishPage({ accountId, smartLinkId }: UsePublishPageProps) {
  const router = useSMLRouter();
  const [publishSmartLink, { isLoading, error }] = usePublishSmartLinkMutation();

  const publish = useCallback(() => {
    publishSmartLink({ smartLinkId, accountId })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          return;
        }
        const { internalCode } = result.error as FYMAPIError;
        if (internalCode === InternalErrorCode.SmartLinkSlugIsTaken) {
          router.push(SMLRoutes.details, { accountId, smartLinkId });
        }
      })
      .catch(console.error);
  }, [accountId, publishSmartLink, router, smartLinkId]);

  return { publish, isLoading, error };
}
